// SegmentPicker.jsx — pick a saved segment OR build an ad-hoc rule tree inline
import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { Button, ButtonGroup, FormGroup, Label } from 'reactstrap';
import { firestore } from '../../../../../helpers/Firebase';
import RuleGroup from '../../segments/components/RuleGroup.jsx';

const selectPortalProps = {
  classNamePrefix: 'rs',
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  styles: { menuPortal: base => ({ ...base, zIndex: 9999 }) },
};

const EMPTY_TREE = () => ({ match: 'all', rules: [{ field: null, op: null, value: undefined }] });

const SegmentPicker = ({ value, onChange }) => {
  // value shape: { segmentId, segmentName, ruleTree }
  // - segmentId+segmentName: reference to saved segment (ruleTree snapshot taken at schedule time)
  // - ruleTree without segmentId: ad-hoc
  const [mode, setMode] = useState(value?.segmentId ? 'saved' : 'adhoc');
  const [segments, setSegments] = useState([]);

  useEffect(() => {
    const unsub = firestore().collection('marketing_segments').orderBy('updatedAt', 'desc').onSnapshot(
      (snap) => setSegments(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      (e) => console.error('segments listener', e)
    );
    return () => unsub();
  }, []);

  const opts = segments.map(s => ({ value: s.id, label: s.name, ruleTree: s.ruleTree }));
  const selected = value?.segmentId ? opts.find(o => o.value === value.segmentId) : null;

  const setSavedSegment = (opt) => {
    if (!opt) onChange({ segmentId: null, segmentName: null, ruleTree: EMPTY_TREE() });
    else onChange({ segmentId: opt.value, segmentName: opt.label, ruleTree: opt.ruleTree });
  };

  const setAdhocTree = (tree) => onChange({ segmentId: null, segmentName: null, ruleTree: tree });

  return (
    <div>
      <FormGroup>
        <Label className="d-block">Audience</Label>
        <ButtonGroup size="sm">
          <Button color="primary" outline={mode !== 'saved'} onClick={() => setMode('saved')}>
            Use saved segment
          </Button>
          <Button color="primary" outline={mode !== 'adhoc'} onClick={() => setMode('adhoc')}>
            Build inline
          </Button>
        </ButtonGroup>
      </FormGroup>

      {mode === 'saved' && (
        <FormGroup>
          <Label className="small">Choose a saved segment</Label>
          <Select
            value={selected}
            onChange={setSavedSegment}
            options={opts}
            placeholder={segments.length ? 'Pick a segment…' : 'No saved segments yet — create one first'}
            isClearable
            isDisabled={segments.length === 0}
            {...selectPortalProps}
          />
          <small className="text-muted">
            A snapshot of the segment's rules is taken when the campaign is scheduled —
            future edits to the segment won't affect this campaign.
          </small>
        </FormGroup>
      )}

      {mode === 'adhoc' && (
        <div>
          <small className="text-muted d-block mb-2">Define audience rules for this campaign only.</small>
          <RuleGroup
            group={value?.ruleTree || EMPTY_TREE()}
            onChange={setAdhocTree}
            onRemove={() => {}}
            depth={0}
          />
        </div>
      )}
    </div>
  );
};

export default SegmentPicker;
